import React, { useMemo, useState, useEffect } from "react";
import { Row, Col, Card, Segmented } from "antd";
import { Column } from "@ant-design/plots";

const STATUS_ORDER = ["Correct", "Incorrect", "Pending"];
const STATUS_COLORS = {
    Correct: "#87e8de",
    Incorrect: "#ffadd2",
    Pending: "#ffe58f",
};
function optionsOf(payload, code) {
    return payload?.[code]?.options || [];
}
function columnsOf(payload, code) {
    return payload?.[code]?.columns || [];
}

function normalizeColumns(rows = [], optionOrder = []) {
    const order = new Map(optionOrder.map((o, i) => [o, i]));
    const rankStatus = (t) => {
        const i = STATUS_ORDER.indexOf(t);
        return i === -1 ? 99 : i;
    };
    return [...rows].sort((a, b) => {
        const ao = order.get(a.option) ?? 9999;
        const bo = order.get(b.option) ?? 9999;
        if (ao !== bo) return ao - bo;
        return rankStatus(a.type) - rankStatus(b.type);
    });
}

const setChartConfig = (rows, optionOrder, setHover) => {
    const data = normalizeColumns(rows, optionOrder);
    return {
        data,
        xField: "option",
        yField: "count",
        colorField: "type",
        seriesField: "type",
        isGroup: true,
        group: { padding: 0 },
        legend: { position: "bottom" },
        color: (d) => STATUS_COLORS[d.type] ?? "#91caff",
        label: {
            text: (d) => (d.count ?? 0).toLocaleString(),
            position: "top",
            style: { fontSize: 12 },
            content: (d) => (d.count ?? 0).toLocaleString(),
        },
        tooltip: {
            fields: ["option", "type", "count"],
            formatter: (d) => ({ name: d.type, value: (d.count ?? 0).toLocaleString() }),
        },
        style: { radiusTopLeft: 4, radiusTopRight: 4 },
        columnStyle: { radius: [4, 4, 0, 0] },
        axis: {
            x: { labelAutoHide: true, title: null },
            y: { labelFormatter: (v) => Number(v).toLocaleString(), title: null },
        },
        paddingLeft: 8,
        paddingRight: 8,
        onReady: (plot) => {
            plot.on("element:mousemove", (evt) => {
                const d = evt?.data?.data;
                if (d) setHover({ option: d.option, type: d.type, count: d.count });
            });
            plot.on("element:mouseleave", () => setHover(null));
        },
    };
};

const PredictionsMarketOptionsColumns = ({ payload, markets, isLoading }) => {
    const [market, setMarket] = useState(markets[0]?.code || "");
    const [hover, setHover] = useState(null);

    useEffect(() => {
        if (markets.length && !markets.find((m) => m.code === market)) {
            setMarket(markets[0].code);
        }
    }, [markets]);

    const opts = useMemo(() => optionsOf(payload, market), [payload, market]);
    const rows = useMemo(() => columnsOf(payload, market), [payload, market]);
    const config = useMemo(() => setChartConfig(rows || [], opts || [], setHover), [rows, opts]);

    return (
        <Card
            title="Prediction options"
            extra={
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <Segmented
                        size="middle"
                        value={market}
                        onChange={setMarket}
                        options={markets.map((m) => ({ label: m.code || m.code, value: m.code }))}
                    />
                    {hover ? (
                        <div style={{ display: "none", gap: 10, alignItems: "center" }}>
                            <b>{hover.option}</b>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                <span
                                    style={{
                                        width: 10,
                                        height: 10,
                                        background: STATUS_COLORS[hover.type] || "#91caff",
                                        display: "inline-block",
                                        borderRadius: 2,
                                    }}
                                />
                                {hover.type}: <b>{hover.count?.toLocaleString?.() ?? hover.count}</b>
                            </span>
                        </div>
                    ) : null}
                </div>
            }
        >
            <Row gutter={24}>
                <Col span={24}>
                    <Column {...config} loading={isLoading} className="p-0 m-0" />
                </Col>
            </Row>
        </Card>
    );
};

export default PredictionsMarketOptionsColumns;
